/**
 * Format an email date for chat display.
 * Today → time only, Yesterday → "Yesterday", else → MM/DD/YYYY
 */
export const formatEmailDate = (date: string | number): string => {
    const d = new Date(typeof date === 'number' ? date * 1000 : date)
    if (isNaN(d.getTime())) return ''

    const now = new Date()
    const isToday = d.toDateString() === now.toDateString()
    const yesterday = new Date(now)
    yesterday.setDate(yesterday.getDate() - 1)
    const isYesterday = d.toDateString() === yesterday.toDateString()

    if (isToday) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    if (isYesterday) return 'Yesterday'
    return d.toLocaleDateString([], { month: '2-digit', day: '2-digit', year: 'numeric' })
}

/**
 * Strip quoted reply history, signatures, and footers from email body.
 * Ported from extension's sidePanel/utils/email.ts
 */
export const truncateEmailHistory = (emailBody: string): string => {
    const patterns = [
        /(?:&#45;|&ndash;|&mdash;|-){2,}\s*On\s+.*?wrote\s*(?:&#45;|&ndash;|&mdash;|-)*/i,
        /On (?:Mon|Tue|Wed|Thu|Fri|Sat|Sun), \d{1,2} (?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{4}.*wrote:/i,
        /On (?:Mon|Tue|Wed|Thu|Fri|Sat|Sun), (?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{1,2}, \d{4}/i,
        /From:.*Sent:.*To:.*Subject:.*/i,
        /From:.*Date:.*Subject:.*/i,
        /_-+ Original Message -+_/i,
    ]

    let earliestIndex = -1

    for (const pattern of patterns) {
        const match = emailBody.match(pattern)
        if (match && match.index !== undefined) {
            if (earliestIndex === -1 || match.index < earliestIndex) {
                earliestIndex = match.index
            }
        }
    }

    const blockquoteIndex = emailBody.indexOf('<blockquote')
    if (blockquoteIndex !== -1) {
        if (earliestIndex === -1 || blockquoteIndex < earliestIndex) {
            earliestIndex = blockquoteIndex
        }
    }

    const hrIndex = emailBody.indexOf('<hr')
    if (hrIndex !== -1) {
        if (earliestIndex === -1 || hrIndex < earliestIndex) {
            earliestIndex = hrIndex
        }
    }

    if (earliestIndex !== -1) {
        return emailBody.substring(0, earliestIndex)
    }

    return emailBody
}

/**
 * Get email HTML content for full rendering (bottom sheet / modal).
 * Matches extension's EmailBody: body → snippet, with truncateEmailHistory.
 * NOTE: cleaned_body is markdown (AI-cleaned), not HTML — skip it for HTML rendering.
 */
export const getEmailFullContent = (
    emailData: { htmlBody?: string; object?: { body?: string; snippet?: string }; cleaned_body?: string },
    fromUs: boolean
): string => {
    if (fromUs) return emailData?.htmlBody || emailData?.object?.body || ''
    const raw = emailData?.object?.body || emailData?.object?.snippet || ''
    return truncateEmailHistory(raw)
}

/**
 * Prepare email snippet for compact bubble preview.
 * Matches extension's prepareSnippetForPreview: strips images & block elements,
 * keeps only text formatting tags for 2-line truncated view.
 */
export const prepareSnippetForPreview = (html: string): string => {
    const temp = document.createElement('div')
    temp.innerHTML = html

    // Replace block elements and br with spaces for text flow
    temp.querySelectorAll('p, div, br').forEach((el) => {
        if (el.tagName === 'BR') {
            el.replaceWith(' ')
        } else if (el.tagName === 'P') {
            const space = document.createTextNode(' ')
            el.parentNode?.insertBefore(space, el.nextSibling)
        }
    })

    // Remove images entirely for compact preview
    temp.querySelectorAll('img').forEach((el) => el.remove())

    return temp.innerHTML.replace(/\s+/g, ' ').replace(/>\s+</g, '> <').trim()
}

/**
 * Check if an email is from our team (user's connected Nylas emails).
 * Also treats pending/failed emails as "ours".
 */
export const isFromOurTeam = (
    email: { emailData?: { object?: { from?: Array<{ email?: string }> } }; isPending?: boolean; isFailed?: boolean },
    userEmails: string[]
): boolean => {
    if (email.isPending || email.isFailed) return true
    const fromEmail = email.emailData?.object?.from?.[0]?.email?.toLowerCase() || ''
    return userEmails.length > 0 && userEmails.includes(fromEmail)
}
